/* ===================================================================
   Static data: deal types, model pricing fallback, workstreams, signatures,
   doc types, agent roster, simulation speeds, filters, phases, eval flags,
   import meta. Extracted from the legacy App monolith.
=================================================================== */
import {
  Inbox,
  Target,
  Layers,
  type LucideIcon,
} from "lucide-react";
import type { ModelDef, WorkstreamLite } from "./types";

export const DEAL_TYPES: string[] = [
  "Series A Preferred Stock Financing", "Series B Preferred Stock Financing", "Series C Preferred Stock Financing",
  "Seed / SAFE Financing", "Convertible Note Financing", "Bridge Financing",
  "Venture Debt Facility", "Senior Secured Credit Facility", "Revolving Credit Facility",
  "Fund Formation — Venture", "Fund Formation — Private Equity", "Fund Formation — Credit", "Co-Investment Vehicle",
  "Stock Purchase / M&A", "Asset Purchase", "Merger", "Recapitalization",
  "Master Services Agreement", "IP License", "Joint Venture",
];

export const ENTITY_KINDS: string[] = [
  "Our firm", "Issuer / Company", "Lead investor", "Investor", "Investor counsel",
  "Company counsel", "Lender", "Regulator", "Other counsel", "Service provider",
];

export const WORKSTREAMS: WorkstreamLite[] = [
  { id: "ws_lumen", name: "Lumen Robotics — Series B", code: "M-2041", type: "Matter", phase: "Setup",
    dealType: "Series B Preferred Stock Financing",
    entities: [
      { id: "e_cw", name: "Carter Whitman LLP", kind: "Our firm", internal: true, people: [
        { name: "Michael Reyes", email: "mreyes@carterwhitman.com", role: "Lead partner" },
        { name: "Priya Nair", email: "pnair@carterwhitman.com", role: "Associate" } ] },
      { id: "e_lumen", name: "Lumen Robotics, Inc.", kind: "Issuer / Company", internal: false, people: [
        { name: "Sarah Chen", email: "sarah.chen@lumenrobotics.com", role: "CEO" },
        { name: "David Okafor", email: "david.okafor@lumenrobotics.com", role: "CFO" } ] },
      { id: "e_ridge", name: "Ridgeline Ventures", kind: "Lead investor", internal: false, people: [
        { name: "Alex Morgan", email: "amorgan@ridgeline.vc", role: "Partner" },
        { name: "Rachel Kim", email: "rkim@ridgeline.vc", role: "Principal" } ] },
      { id: "e_by", name: "Bradford Yale LLP", kind: "Investor counsel", internal: false, people: [
        { name: "Jonathan Hsu", email: "jhsu@bradfordyale.com", role: "Partner" },
        { name: "Elena Vasquez", email: "evasquez@bradfordyale.com", role: "Associate" } ] },
    ],
    memoFields: [
      { k: "Round size", v: "$45M" }, { k: "Pre-money valuation", v: "$180M" }, { k: "Security", v: "Series B Preferred" },
      { k: "Liquidation preference", v: "1x non-participating" }, { k: "Option pool", v: "11% post" }, { k: "Board seats", v: "1 investor seat" },
    ],
    capTable: { attached: true, name: "Lumen_captable_pre-close.xlsx" },
    documents: [
      { id: "doc_ts", name: "Term Sheet", doc_type: "term_sheet", current_version: 2, status: "executed",
        bears_on: ["round_size", "pre_money", "liquidation_preference", "option_pool"],
        versions: [
          { version: 1, source_email_id: 1, date: "Apr 06, 2026", filename: "Lumen_Term_Sheet_v1.docx" },
          { version: 2, source_email_id: 5, date: "Apr 10, 2026", filename: "Lumen_Term_Sheet_signed.pdf" },
        ] },
      { id: "doc_spa", name: "Stock Purchase Agreement", doc_type: "spa", current_version: 1, status: "executed",
        bears_on: ["round_size", "pre_money", "price_per_share"],
        versions: [
          { version: 1, source_email_id: 7, date: "Apr 17, 2026", filename: "Lumen_SPA_v1.docx" },
        ] },
      { id: "doc_charter", name: "Certificate of Incorporation", doc_type: "charter", current_version: 3, status: "conformed",
        bears_on: ["liquidation_preference", "anti_dilution", "dividend_rate", "board_composition"],
        versions: [
          { version: 1, source_email_id: 7, date: "Apr 17, 2026", filename: "Lumen_Charter_v1.docx" },
          { version: 2, source_email_id: 14, date: "Apr 22, 2026", filename: "Lumen_Charter_v2_redline.docx" },
          { version: 3, source_email_id: 14, date: "Apr 22, 2026", filename: "Lumen_Charter_v3_clean.docx" },
        ] },
      { id: "doc_ira", name: "Investors' Rights Agreement", doc_type: "ira", current_version: 1, status: "executed",
        bears_on: ["information_rights", "registration_rights", "preemptive_rights"],
        versions: [
          { version: 1, source_email_id: 7, date: "Apr 17, 2026", filename: "Lumen_IRA_v1.docx" },
        ] },
      { id: "doc_rofr", name: "ROFR / Co-Sale Agreement", doc_type: "rofr", current_version: 1, status: "executed",
        bears_on: ["transfer_restrictions", "cosale_rights"],
        versions: [
          { version: 1, source_email_id: 7, date: "Apr 17, 2026", filename: "Lumen_ROFR_CoSale_v1.docx" },
        ] },
      { id: "doc_voting", name: "Voting Agreement", doc_type: "voting_agreement", current_version: 1, status: "executed",
        bears_on: ["board_composition", "drag_along"],
        versions: [
          { version: 1, source_email_id: 7, date: "Apr 17, 2026", filename: "Lumen_Voting_Agreement_v1.docx" },
        ] },
      { id: "doc_captable", name: "Pre-Close Cap Table", doc_type: "cap_table", current_version: 1, status: "under_review",
        bears_on: ["pre_money", "post_money", "price_per_share", "option_pool"],
        versions: [
          { version: 1, source_email_id: 10, date: "May 04, 2026", filename: "Lumen_captable_pre-close.xlsx" },
        ] },
      { id: "doc_exec", name: "Executed Document Package", doc_type: "executed_package", current_version: 1, status: "executed",
        bears_on: ["all_definitive_terms"],
        versions: [
          { version: 1, source_email_id: 13, date: "May 12, 2026", filename: "Lumen_Execution_Package_signed.pdf" },
        ] },
    ] },
  { id: "ws_acme", name: "Acme Health — MSA", code: "M-1830", type: "Matter", phase: "Documentation",
    dealType: "Master Services Agreement",
    entities: [
      { id: "e_cw2", name: "Carter Whitman LLP", kind: "Our firm", internal: true, people: [
        { name: "Michael Reyes", email: "mreyes@carterwhitman.com", role: "Lead partner" } ] },
      { id: "e_lumen2", name: "Lumen Robotics, Inc.", kind: "Issuer / Company", internal: false, people: [
        { name: "David Okafor", email: "david.okafor@lumenrobotics.com", role: "CFO" } ] },
      { id: "e_acme", name: "Acme Health Systems", kind: "Service provider", internal: false, people: [
        { name: "Procurement Team", email: "procurement@acmehealth.com", role: "Vendor procurement" } ] },
    ],
    memoFields: [{ k: "Term", v: "3 years" }, { k: "Pricing", v: "5% annual uplift" }],
    capTable: { attached: false, name: "" } },
];

export const WS_NAME: Record<string, string> = {
  ws_lumen: "Lumen — Series B",
  ws_acme: "Acme — MSA",
  unassoc: "Unassociated",
};

/* Checks carry concrete OpenRouter model ids — cost is METERED from tokens,
   never set by hand. Live names/prices come from the /models proxy; this
   static table is the offline/dev fallback. */
export const DEFAULT_MODEL_ID = "anthropic/claude-sonnet-4.6";

export const FALLBACK_MODEL_PRICES: Record<string, ModelDef> = {
  "anthropic/claude-haiku-4.5":
    { id: "anthropic/claude-haiku-4.5", name: "Claude Haiku 4.5", inP: 0.80, outP: 4.00 },
  "anthropic/claude-sonnet-4.6":
    { id: "anthropic/claude-sonnet-4.6", name: "Claude Sonnet 4.6", inP: 3.00, outP: 15.00 },
  "anthropic/claude-opus-4.8":
    { id: "anthropic/claude-opus-4.8", name: "Claude Opus 4.8", inP: 15.00, outP: 75.00 },
  "openai/gpt-5.6-luna":
    { id: "openai/gpt-5.6-luna", name: "OpenAI: GPT-5.6 Luna", inP: 0.20, outP: 1.20 },
};

/* Read-time aliases for the retired model-tier ids — stale CheckVersion
   snapshots and persisted drafts may still carry them. */
export const LEGACY_MODEL_ALIAS: Record<string, string> = {
  m_small: DEFAULT_MODEL_ID,
  m_std: DEFAULT_MODEL_ID,
  m_large: DEFAULT_MODEL_ID,
  m_frontier: DEFAULT_MODEL_ID,
};

export function resolveModelId(id: string | undefined | null): string {
  const m = id || "";
  return LEGACY_MODEL_ALIAS[m] || m || DEFAULT_MODEL_ID;
}

/* Display name + $/M prices for a model id: live listing first, then the
   static fallback, then the default model's price under the raw id. */
export function modelInfo(
  id: string | undefined | null,
  liveModels?: Array<{ id: string; name: string; inP: number; outP: number }> | null,
): ModelDef {
  const slug = resolveModelId(id);
  const live = (liveModels || []).find((m) => m.id === slug);
  if (live && (live.inP > 0 || live.outP > 0)) return { ...live };
  return (
    FALLBACK_MODEL_PRICES[slug] || {
      ...FALLBACK_MODEL_PRICES[DEFAULT_MODEL_ID],
      id: slug,
      name: live?.name || slug,
    }
  );
}

/* The downstream-agent roster now lives in the unified check registry —
   see AGENT_GROUPS / AGENT_CHECK_IDS in ./checks. */

/* semantic color for a tag/status */
export const COL: Record<string, string> = {
  conflict: "--red", anomaly: "--red", privilege: "--red", multi: "--blue", "new-participant": "--blue",
  memo: "--amber", doc: "--blue", log: "--low", noise: "--low", pass: "--green", flag: "--amber", fail: "--red",
};

/* signatures + disclaimers */
export const SIG: Record<string, string> = {
  amorgan: "--\nAlex Morgan\nPartner, Ridgeline Ventures\namorgan@ridgeline.vc · ridgeline.vc",
  rkim: "--\nRachel Kim\nPrincipal, Ridgeline Ventures\nrkim@ridgeline.vc",
  ben: "--\nBen Foster\nRidgeline Ventures\nbfoster@ridgeline.vc",
  sarah: "--\nSarah Chen\nCo-founder & CEO, Lumen Robotics, Inc.\nsarah.chen@lumenrobotics.com",
  david: "--\nDavid Okafor\nChief Financial Officer, Lumen Robotics, Inc.\ndavid.okafor@lumenrobotics.com · (415) 555-0147",
  pnair: "--\nPriya Nair\nSenior Associate · Carter Whitman LLP\n1271 Avenue of the Americas, New York, NY 10020\nT +1 212 555 0182 · pnair@carterwhitman.com",
  mreyes: "--\nMichael Reyes\nPartner · Carter Whitman LLP\nT +1 212 555 0170 · mreyes@carterwhitman.com",
  jhsu: "--\nJonathan Hsu\nPartner · Bradford Yale LLP\nT +1 415 555 0090 · jhsu@bradfordyale.com",
  evasquez: "--\nElena Vasquez\nAssociate · Bradford Yale LLP\nT +1 415 555 0093 · evasquez@bradfordyale.com",
  acme: "--\nProcurement Team\nAcme Health\nprocurement@acmehealth.com",
  dataroom: "--\nJess Romero · Account Executive\nDataRoomPro · dataroompro.io",
};
export const DISC_CW = "CONFIDENTIALITY NOTICE: This e-mail and any attachments are confidential, may be subject to the attorney-client privilege or work-product doctrine, and are intended solely for the named recipient(s). If you are not the intended recipient, any disclosure, copying, or distribution is prohibited; please notify the sender and delete all copies. Carter Whitman LLP.";
export const DISC_BY = "PRIVILEGED & CONFIDENTIAL: This message is intended only for the use of the addressee and may contain information that is privileged and confidential. If you are not the intended recipient, you are hereby notified that any dissemination of this communication is strictly prohibited. Bradford Yale LLP.";
export const DISC_CO = "This message and any files transmitted with it are confidential and intended solely for the use of the individual or entity to whom they are addressed.";

/* Legal document type labels */
export const DOC_TYPES: Array<[string, string]> = [
  ["term_sheet", "Term Sheet"],
  ["spa", "Stock Purchase Agreement"],
  ["charter", "Certificate of Incorporation"],
  ["ira", "Investors' Rights Agreement"],
  ["rofr", "ROFR / Co-Sale Agreement"],
  ["voting_agreement", "Voting Agreement"],
  ["side_letter", "Side Letter"],
  ["board_consent", "Board Consent"],
  ["cap_table", "Cap Table"],
  ["opinion_letter", "Opinion Letter"],
  ["engagement_letter", "Engagement Letter"],
  ["nda", "NDA"],
  ["disclosure_schedule", "Disclosure Schedule"],
  ["closing_checklist", "Closing Checklist"],
  ["msa", "Master Services Agreement"],
  ["other", "Other"],
];
export const DOC_TYPE_LABEL: Record<string, string> = Object.fromEntries(DOC_TYPES);

export const SPEEDS: Array<[string, number]> = [
  ["1×", 1800], ["2×", 900], ["4×", 420], ["⚡", 140],
];
export const FILTERS: Array<[string, string]> = [
  ["all", "All"], ["anom", "Conflicts"], ["multi", "Multi"], ["priv", "Privilege"], ["memo", "Deal-memo"],
];

export const PHASES: string[] = ["Setup", "Terms", "Documentation", "Pre-Closing", "Post-Closing"];

export const EVAL_FLAGS: Array<[string, string]> = [
  ["conflict", "conflict"], ["multi", "multi"], ["privilege", "privilege"],
  ["newpart", "new-participant"], ["phase", "phase"], ["noise", "noise"],
];

/* Import-modal metadata: label, icon, blank description, file-types accepted */
export interface ImportMetaEntry {
  label: string;
  icon: LucideIcon;
  blankDesc: string;
  accept: string;
}
export const IMPORT_META: Record<string, ImportMetaEntry> = {
  corpus: { label: "Email Corpus", icon: Inbox, blankDesc: "Create an empty corpus and add emails by hand.", accept: "mbox · eml · zip · json · csv · pdf · txt · docx + attachment files (pdf · docx · xlsx …)" },
  eval: { label: "Eval Set", icon: Target, blankDesc: "Create an empty answer key for a corpus.", accept: "json · csv · txt" },
  workstream: { label: "Workstream Bundle", icon: Layers, blankDesc: "Create an empty bundle and add workstreams by hand.", accept: "json · txt · pdf · docx (engagement letters, term sheets)" },
};
export function typeLabel(t: string): string {
  return t === "corpus" ? "email corpus" : t === "eval" ? "eval set" : "workstream";
}
